import { apiClient } from '@/utils/api-client'

/**
 * Target service for managing flag targets (deployments).
 *
 * Targets represent specific configurations of flags in environments,
 * including rules and deployment schedules.
 */
class TargetService {
    /**
     * Clone an existing target. The response includes the copied rules.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID to clone
     * @returns {Promise<Object>} Cloned target data, with its rules under `rules.data`
     * @throws {string} Error message if cloning fails
     */
    async clone(projectKey, environmentKey, targetUlid) {
        return await apiClient
            .post(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '?include=rules',
                {}
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Create a new target for a flag.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} flagKey - Flag key
     * @param {string} valueUlid - Flag value ULID
     * @param {Object} form - Target configuration
     * @param {Array} [form.rules] - Targeting rules
     * @param {string} [form.publish_at] - Scheduled publish time
     * @returns {Promise<Object>} Created target data
     * @throws {string} Error message if creation fails
     */
    async create(projectKey, environmentKey, flagKey, valueUlid, form) {
        form['value_ulid'] = valueUlid
        form['flag_key'] = flagKey

        return await apiClient
            .post(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/target',
                form
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Delete a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID to delete
     * @returns {Promise<Object>} Deletion confirmation
     * @throws {string} Error message if deletion fails
     */
    async delete(projectKey, environmentKey, targetUlid) {
        return await apiClient
            .delete(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get a single target by ULID.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @returns {Promise<Object>} Target data with rules
     * @throws {string} Error message if target not found
     */
    async get(projectKey, environmentKey, targetUlid) {
        return await apiClient
            .get(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get the last-retrieval (usage) data for a flag.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} flagKey - Flag key
     * @returns {Promise<Object>} Last retrieval data
     * @throws {string} Error message if request fails
     */
    async lastRetrieval(projectKey, environmentKey, flagKey) {
        return await apiClient
            .get(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets' +
                    '/last-retrieval?flag_key=' +
                    flagKey
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get all targets for a flag.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} flagKey - Flag key
     * @returns {Promise<Array>} Array of target objects
     * @throws {string} Error message if request fails
     */
    async list(projectKey, environmentKey, flagKey) {
        return await apiClient
            .get(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets?flag_key=' +
                    flagKey
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get all draft targets for a flag.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} flagKey - Flag key
     * @returns {Promise<Array>} Array of draft target objects
     * @throws {string} Error message if request fails
     */
    async listDrafts(projectKey, environmentKey, flagKey) {
        return await apiClient
            .get(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets?filter=drafts&flag_key=' +
                    flagKey
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get all scheduled targets for a flag.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} flagKey - Flag key
     * @returns {Promise<Array>} Array of scheduled target objects
     * @throws {string} Error message if request fails
     */
    async listScheduled(projectKey, environmentKey, flagKey) {
        return await apiClient
            .get(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets?filter=scheduled&flag_key=' +
                    flagKey
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Publish a target (update with new configuration).
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID to publish
     * @param {Object} form - Updated target configuration
     * @returns {Promise<Object>} Updated target data
     * @throws {string} Error message if publish fails
     */
    async publish(projectKey, environmentKey, targetUlid, form) {
        return await apiClient
            .put(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid,
                form
            )
            .then((response) => {
                return response.data
            })
    }
}

export default new TargetService()
